import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Save, Trash2, Package } from "lucide-react";

interface PriceItem {
  id: string;
  item_name: string;
  unit: string;
  price_per_unit: number;
  notes: string | null;
  is_active: boolean;
}

interface NewItem {
  item_name: string;
  unit: string;
  price_per_unit: string;
  notes: string;
}

const COMMON_UNITS = ["m²", "bm", "ks", "hod", "m³", "kg", "komplet"];

interface Props {
  supplierId: string;
  serviceSlug: string;
  serviceName: string;
}

export function GenericPriceCatalog({ supplierId, serviceSlug, serviceName }: Props) {
  const [items, setItems] = useState<PriceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState<NewItem>({ item_name: "", unit: "m²", price_per_unit: "", notes: "" });

  // Service description
  const [description, setDescription] = useState("");
  const [descriptionId, setDescriptionId] = useState<string | null>(null);
  const [savingDesc, setSavingDesc] = useState(false);
  const descTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    loadItems();
    loadDescription();
  }, [supplierId, serviceSlug]);

  const loadDescription = async () => {
    const { data, error } = await supabase
      .from("supplier_service_descriptions" as any)
      .select("*")
      .eq("supplier_id", supplierId)
      .eq("service_slug", serviceSlug)
      .maybeSingle();
    if (!error && data) {
      setDescription((data as any).description || "");
      setDescriptionId((data as any).id);
    }
  };

  const saveDescription = async (text: string) => {
    setSavingDesc(true);
    if (descriptionId) {
      // Update existing
      const { error } = await supabase
        .from("supplier_service_descriptions" as any)
        .update({ description: text, updated_at: new Date().toISOString() } as any)
        .eq("id", descriptionId);
      if (error) toast.error("Nepodařilo se uložit popis");
    } else {
      // Insert new
      const { data, error } = await supabase
        .from("supplier_service_descriptions" as any)
        .insert({
          supplier_id: supplierId,
          service_slug: serviceSlug,
          description: text,
        } as any)
        .select("id")
        .single();
      if (error) {
        toast.error("Nepodařilo se uložit popis");
      } else {
        setDescriptionId((data as any).id);
      }
    }
    setSavingDesc(false);
  };

  const onDescriptionChange = (text: string) => {
    setDescription(text);
    // Auto-save after 800ms of inactivity
    if (descTimer.current) clearTimeout(descTimer.current);
    descTimer.current = setTimeout(() => saveDescription(text), 800);
  };

  const loadItems = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("supplier_service_prices" as any)
      .select("*")
      .eq("supplier_id", supplierId)
      .eq("service_slug", serviceSlug)
      .order("created_at", { ascending: true });
    if (error) {
      console.error("Load service prices error:", error);
    }
    setItems((data as any as PriceItem[]) || []);
    setLoading(false);
  };

  const addItem = async () => {
    const name = newItem.item_name.trim();
    const price = parseFloat(newItem.price_per_unit);
    if (!name || !price || price <= 0) {
      toast.error("Vyplňte název a cenu");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("supplier_service_prices" as any)
      .insert({
        supplier_id: supplierId,
        service_slug: serviceSlug,
        item_name: name,
        unit: newItem.unit || "m²",
        price_per_unit: price,
        notes: newItem.notes.trim() || null,
      } as any);
    if (error) {
      if ((error as any).code === "23505") {
        toast.error("Položka s tímto názvem už existuje");
      } else {
        toast.error("Nepodařilo se přidat");
      }
    } else {
      toast.success("Položka přidána");
      setNewItem({ item_name: "", unit: "m²", price_per_unit: "", notes: "" });
      loadItems();
    }
    setSaving(false);
  };

  const updateItem = async (id: string, updates: Partial<PriceItem>) => {
    const { error } = await supabase
      .from("supplier_service_prices" as any)
      .update({ ...updates, updated_at: new Date().toISOString() } as any)
      .eq("id", id);
    if (error) {
      toast.error("Nepodařilo se uložit");
    } else {
      setItems(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
    }
  };

  const deleteItem = async (id: string) => {
    const { error } = await supabase
      .from("supplier_service_prices" as any)
      .delete()
      .eq("id", id);
    if (error) {
      toast.error("Nepodařilo se smazat");
    } else {
      setItems(prev => prev.filter(item => item.id !== id));
      toast.success("Smazáno");
    }
  };

  if (loading) {
    return <div className="py-6 text-center text-muted-foreground text-sm animate-pulse">Načítám ceník…</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <Package className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">{serviceName}</h3>
        <span className="text-xs text-muted-foreground">({items.length} položek)</span>
      </div>

      {/* Service description */}
      <div className="space-y-1.5">
        <label className="text-xs text-muted-foreground font-medium">Popis služby</label>
        <textarea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          rows={3}
          placeholder={`Popište, co v rámci služby „${serviceName}" nabízíte – rozsah prací, materiály, garance…`}
          className="w-full text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-primary/20 resize-none"
        />
        {savingDesc && (
          <p className="text-[11px] text-muted-foreground animate-pulse">Ukládám…</p>
        )}
      </div>

      {/* Existing items */}
      {items.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground font-medium">Ceníkové položky</p>
          {items.map((item) => (
            <div key={item.id} className="flex items-center gap-2 border border-border rounded-lg p-2.5 bg-card">
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={item.item_name}
                  onChange={(e) => setItems(prev => prev.map(i => i.id === item.id ? { ...i, item_name: e.target.value } : i))}
                  onBlur={(e) => updateItem(item.id, { item_name: e.target.value.trim() })}
                  className="w-full text-sm bg-transparent text-foreground outline-none font-medium"
                />
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <input
                  type="number"
                  value={item.price_per_unit}
                  onChange={(e) => setItems(prev => prev.map(i => i.id === item.id ? { ...i, price_per_unit: parseFloat(e.target.value) || 0 } : i))}
                  onBlur={(e) => updateItem(item.id, { price_per_unit: parseFloat(e.target.value) || 0 })}
                  className="w-20 text-sm bg-background border border-border rounded px-2 py-1 text-right text-foreground outline-none"
                />
                <span className="text-xs text-muted-foreground w-8">Kč/</span>
                <select
                  value={item.unit}
                  onChange={(e) => updateItem(item.id, { unit: e.target.value })}
                  className="text-xs bg-background border border-border rounded px-1 py-1 text-foreground outline-none"
                >
                  {COMMON_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <button
                onClick={() => deleteItem(item.id)}
                className="p-1 text-muted-foreground hover:text-red-500 transition-colors shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          Zatím nemáte žádné ceníkové položky pro tuto službu.
        </p>
      )}

      {/* Add new item */}
      <div className="border border-dashed border-border rounded-lg p-3 space-y-2">
        <p className="text-xs text-muted-foreground font-medium">Přidat položku</p>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={newItem.item_name}
            onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
            placeholder="Název položky"
            className="flex-1 min-w-[150px] text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground outline-none focus:ring-2 focus:ring-primary/20"
          />
          <input
            type="number"
            value={newItem.price_per_unit}
            onChange={(e) => setNewItem({ ...newItem, price_per_unit: e.target.value })}
            placeholder="Cena"
            className="w-24 text-sm bg-background border border-border rounded-lg px-3 py-2 text-foreground outline-none focus:ring-2 focus:ring-primary/20"
          />
          <select
            value={newItem.unit}
            onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
            className="text-sm bg-background border border-border rounded-lg px-2 py-2 text-foreground outline-none"
          >
            {COMMON_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
          <button
            onClick={addItem}
            disabled={saving || !newItem.item_name.trim() || !newItem.price_per_unit}
            className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground text-sm rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            <Plus className="w-3.5 h-3.5" />
            Přidat
          </button>
        </div>
      </div>
    </div>
  );
}
